import { useCallback, useSyncExternalStore } from 'react';

export interface ProblemProgress {
  code?: string;
  solved?: boolean;
  attempted?: boolean;
}

type ProgressMap = Record<string, ProblemProgress>;

const STORAGE_KEY = 'jsbat:progress';
const listeners = new Set<() => void>();

function load(): ProgressMap {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as ProgressMap) : {};
  } catch {
    return {};
  }
}

let state: ProgressMap = load();

function emit() {
  listeners.forEach((l) => l());
}

function save(next: ProgressMap) {
  state = next;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Storage may be full or unavailable; keep in-memory state.
  }
  emit();
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEY) {
      state = load();
      emit();
    }
  });
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function progressKey(categoryId: string, problemId: string) {
  return `${categoryId}/${problemId}`;
}

export function useProgress() {
  const progress = useSyncExternalStore(subscribe, () => state, () => state);

  const update = useCallback((key: string, patch: ProblemProgress) => {
    save({ ...state, [key]: { ...state[key], ...patch } });
  }, []);

  const reset = useCallback(() => save({}), []);

  return { progress, update, reset };
}
